import { organizationsApi, type Organization } from '@/services/api/domains/organizations.api'
import { createContext, useContext, useEffect, type ReactNode } from 'react'

import { setCartoApiKey } from '../config/carto'
import { useApiQuery } from './useApiQuery'
import { useAuth } from './useAuth'

interface BrandingValues {
  primaryColor: string
  secondaryColor: string
  logoUrl: string | null
  defaultMapEngine: 'openlayers' | 'arcgis' | null
  /**
   * Org-level ArcGIS / Esri API key. Sourced from
   * `Organization.settings.apiKeys.arcgis`. Used by the Map engine to
   * unlock Esri 3D basemaps, world-elevation, and OSM SceneLayers.
   * `null` when the org has no key configured — the engine still renders
   * against community basemaps.
   */
  arcgisApiKey: string | null
  /**
   * Org-level CARTO basemap key, from `Organization.settings.apiKeys.carto`.
   * An on-prem override for the per-deployment `/config.json` key — cloud
   * tenants always see `null` here (get-me blanks `apiKeys` for them) and use
   * the deployment key instead. Not consumed through this context directly:
   * it is pushed into the `config/carto.ts` singleton by the effect below,
   * because `getPresetById()` is called from module scope where no hook value
   * is reachable.
   */
  cartoApiKey: string | null
  refetch: () => void
}

const BrandingContext = createContext<BrandingValues>({
  primaryColor: '#1890ff',
  secondaryColor: '#10B981',
  logoUrl: null,
  defaultMapEngine: null,
  arcgisApiKey: null,
  cartoApiKey: null,
  refetch: () => {},
})

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { data, refetch } = useApiQuery<Organization>(() => organizationsApi.get(), {
    enabled: isAuthenticated,
  })

  const value: BrandingValues = {
    primaryColor: data?.settings?.primaryColor || '#1890ff',
    secondaryColor: data?.settings?.secondaryColor || '#10B981',
    logoUrl: data?.settings?.logoUrl || null,
    defaultMapEngine: (data?.settings?.defaultMapEngine as 'openlayers' | 'arcgis' | undefined) ?? null,
    // `||` (not `??`) so an empty-string value coerces to null. The type
    // contract is `string | null` and downstream callers (`Map.tsx`'s
    // `!!arcgisApiKey`, the engine's `esriConfig.apiKey` assignment) treat
    // `''` as "configured" — `||` aligns the runtime value with the type.
    arcgisApiKey: data?.settings?.apiKeys?.arcgis || null,
    cartoApiKey: data?.settings?.apiKeys?.carto || null,
    refetch,
  }

  // Push the org override into the module singleton the basemap presets read.
  // Keyed off the resolved value so it re-runs only when the org key actually
  // changes, and setCartoApiKey(null) is a safe no-op that leaves the
  // deployment key from /config.json in force.
  useEffect(() => {
    setCartoApiKey(value.cartoApiKey)
  }, [value.cartoApiKey])

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

export const useBranding = () => useContext(BrandingContext)
